import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAgents from "./pages/AdminAgents";
import AdminTasks from "./pages/AdminTasks";
import Leads from "./pages/Leads";
import LeadDetail from "./pages/LeadDetail";
import Campaigns from "./pages/Campaigns";
import Commissions from "./pages/Commissions";
import Contacts from "./pages/Contacts";
import Activities from "./pages/Activities";
import Referrals from "./pages/Referrals";
import Settings from "./pages/Settings";
import Guidelines from "./pages/Guidelines";
import Media from "./pages/Media";
import Videos from "./pages/Videos";
import Users from "./pages/Users";
import Calendar from "./pages/Calendar";
import Onboarding from "./pages/Onboarding";
import DocumentTemplates from "./pages/DocumentTemplates";
import OnboardingDocs from "./pages/OnboardingDocs";
import Layout from "./components/Layout";
import AIChat from "./components/AIChat";
import axios from "axios";
import "./App.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children }) => {
  const { user, loading, token } = useAuth();
  const [onboardingDone, setOnboardingDone] = useState(null);
  const [checking, setChecking] = useState(true);

  const checkOnboarding = useCallback(async () => {
    if (!user || !token) return; // Wait for auth to load
    // Admins skip onboarding
    if (user.role === 'admin') { setOnboardingDone(true); setChecking(false); return; }
    // Check if user already completed onboarding
    if (user.onboarding_completed) { setOnboardingDone(true); setChecking(false); return; }
    try {
      const res = await axios.get(`${API_URL}/api/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
      // Allow access if onboarding is completed OR saved for later
      setOnboardingDone(res.data.completed || res.data.saved_for_later === true);
    } catch {
      setOnboardingDone(true); // fail-open so they aren't stuck
    }
    setChecking(false);
  }, [user, token]);

  useEffect(() => { checkOnboarding(); }, [checkOnboarding]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-[#1B365D] text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-pulse text-[#1B365D] text-lg">Loading...</div>
      </div>
    );
  }

  if (onboardingDone === false) {
    return <Onboarding onComplete={() => setOnboardingDone(true)} />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

// Wrapper for onboarding wizard accessed from within the app (OnboardingDocs page)
const OnboardingWizardPage = () => {
  return <Onboarding onComplete={() => { window.location.href = '/onboarding-docs'; }} isResume />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{ style: { fontFamily: 'Inter, sans-serif' } }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
              <AIChat />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="leads" element={<Leads />} />
            <Route path="leads/:id" element={<LeadDetail />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="commissions" element={<Commissions />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="activities" element={<Activities />} />
            <Route path="referrals" element={<Referrals />} />
            <Route path="settings" element={<Settings />} />
            <Route path="guidelines" element={<Guidelines />} />
            <Route path="media" element={<Media />} />
            <Route path="videos" element={<Videos />} />
            <Route path="calendar" element={<Calendar />} />
            <Route path="onboarding-docs" element={<OnboardingDocs />} />
            <Route path="onboarding-wizard" element={<OnboardingWizardPage />} />
            <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
            <Route path="templates" element={<AdminRoute><DocumentTemplates /></AdminRoute>} />
            <Route path="admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="admin/agents" element={<AdminRoute><AdminAgents /></AdminRoute>} />
            <Route path="admin/tasks" element={<AdminRoute><AdminTasks /></AdminRoute>} />
            <Route path="admin/invitations" element={<AdminRoute><Users /></AdminRoute>} />
            <Route path="admin/payouts" element={<AdminRoute><Commissions /></AdminRoute>} />
            <Route path="admin/audit" element={<AdminRoute><Users /></AdminRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
